import moment from "moment";
import { MAX_RAILWAY_WAGONS_COUNT, TRANSPORTATION_TYPES } from "./railwayOrder";

export const ASSIGNMENT_METHODS = ["Tender", "Auction", "DirectAssignment"];
export const INCOTERMS_CODES = ["EXW", "FCA", "CPT", "CIP", "DAP", "DPU", "DDP", "FAS", "FOB", "CFR", "CIF"];

export const getTenderDeadline = (date, time) => {
  if (!date || !time || !moment(date).isValid() || !moment(time).isValid()) return null;
  return moment(date).set({ hour: moment(time).hour(), minute: moment(time).minute(), second: 0, millisecond: 0 })
    .utc().format();
};

export const buildNewOrderPayload = (data, options) => {
  const { assignmentMethod, isComplexTender, vehiclesCount, tenderDateEnd, tenderEndTime,
    partner, partnersForTender, incotermsEnabled, incoterms, shippingDateTo } = options;
  const direct = assignmentMethod === "DirectAssignment";
  const group = !direct && isComplexTender;
  const isInternational = Boolean(data.countryIdFrom && data.countryIdTo && data.countryIdFrom !== data.countryIdTo);
  const deadline = direct ? null : getTenderDeadline(tenderDateEnd, tenderEndTime);
  const { expeditors, shippingDateTo: previousShippingDateTo, ...order } = data;

  return {
    tenderDateEnd: deadline,
    order: {
      ...order,
      transportationType: options.isRailway ? TRANSPORTATION_TYPES.RAILWAY : TRANSPORTATION_TYPES.ROAD,
      ...(options.isRailway ? {
        wagonTypeId: options.wagonType?.id ?? null,
        wagonsCount: group ? 0 : Number(options.wagonsCount || 0),
        etsngCode: options.etsngCode || null,
        gngCode: options.gngCode || null,
      } : {}),
      assignmentMethod,
      bidReductionStep: assignmentMethod === "Auction" && data.bidReductionStep != null && data.bidReductionStep !== ""
        ? Number(String(data.bidReductionStep).replace(",", ".")) : null,
      ...(group ? {
        shippingDateTo: shippingDateTo ? moment(shippingDateTo).format("YYYY-MM-DD") : null,
      } : {}),
      expeditorId: direct ? partner?.expeditorCompanyId : null,
      tenderDateEnd: deadline,
      price: direct ? Number(data.price) : 0,
      netPrice: direct ? Number(data.price) : 0,
      weight: Number(data.weight),
      volume: Number(data.volume || 0),
      pallet: Number(data.pallet || 0),
      copyCount: direct ? Number(data.copyCount ?? 1) : 1,
      currency: data.acceptedCurrency,
      acceptedCurrency: data.acceptedCurrency,
      incoterms: isInternational && incotermsEnabled ? incoterms : null,
      cargoPrice: data.insuranceIncluded ? Number(data.cargoPrice) : 0,
      insuranceSum: data.insuranceIncluded ? Number(data.insuranceSum || 0) : 0,
      ...(options.isRailway ? {
        vehicleType: null,
        loadingTypeId: null,
        pallet: null,
        insuranceIncluded: false,
        insurancePrice: null,
        insuranceSum: 0,
        cargoPrice: 0,
        declaredPrice: null,
        isWithoutExecutor: true,
      } : {}),
    },
    expeditors: direct ? [] : partnersForTender.map((item) => item.expeditorCompanyId),
    isComplexTender: Boolean(group),
    vehiclesCount: group ? Number(vehiclesCount) : null,
  };
};

export const validateNewOrder = (p, t, now = new Date()) => {
  const errors = {};
  const required = t("ordersCreate.validation.fillData");
  for (const name of ["countryFrom", "countryTo", "cityFrom", "cityTo", "addressFrom", "addressTo",
    "pickUpDate", p.isRailway ? "wagonType" : "type", "category"]) {
    if (!p[name]) errors[name] = required;
  }
  if (!p.isComplexTender) {
    for (const name of ["pickUpStartTime", "pickUpEndTime"]) {
      if (!p[name]) errors[name] = required;
    }
  }
  if (!p.addressIdFrom) errors.addressFrom = required;
  if (!p.addressIdTo) errors.addressTo = required;
  for (const name of ["weight", "volume", "pallet"]) {
    const value = p[name];
    const isEmpty = value === "" || value == null;
    if ((name === "weight" && !p.isRailway && !(Number(value) > 0)) || (!isEmpty &&
      (!Number.isFinite(Number(value)) || Number(value) < 0 || String(value).length > 8))) errors[name] = required;
  }
  if (!p.isRailway && p.type?.value === "refrigerator" && !p.temperature) errors.temperature = required;
  if (p.isRailway) {
    if (!p.wagonType?.id) errors.wagonType = required;
    if (!p.isComplexTender && p.wagonsCount !== "" && p.wagonsCount != null &&
      (!Number.isSafeInteger(Number(p.wagonsCount)) || Number(p.wagonsCount) < 0)) {
      errors.wagonsCount = required;
    } else if (!p.isComplexTender && Number(p.wagonsCount) > MAX_RAILWAY_WAGONS_COUNT) {
      errors.wagonsCount = t("railwayOrder.wagonsCountTooLarge");
    }
  }
  if (p.insurance) {
    if (!(Number(p.declaredPrice) > 0)) errors.declaredPrice = required;
    if (!p.fileInvoice?.name) errors.fileInvoice = t("ordersCreate.validation.uploadInvoice");
  }
  const isInternational = Boolean(p.countryFrom?.id && p.countryTo?.id && p.countryFrom.id !== p.countryTo.id);
  if (isInternational && p.incotermsEnabled && !INCOTERMS_CODES.includes(p.incoterms)) {
    errors.incoterms = required;
  }
  if (p.isComplexTender && (!Number.isSafeInteger(Number(p.vehiclesCount)) || Number(p.vehiclesCount) < 1)) {
    errors.vehiclesCount = required;
  }
  if (p.isComplexTender && p.assignmentMethod !== "DirectAssignment") {
    if (p.pickUpDate && !moment(p.pickUpDate).isValid()) errors.pickUpDate = required;
    if (!p.shippingDateTo || !moment(p.shippingDateTo).isValid() ||
      (p.pickUpDate && moment(p.shippingDateTo).isBefore(p.pickUpDate, "day"))) {
      errors.shippingDateTo = required;
    }
  }
  if (!ASSIGNMENT_METHODS.includes(p.assignmentMethod)) errors.assignmentMethod = required;
  if (p.assignmentMethod === "Auction" && p.auctionStep !== "" && p.auctionStep != null &&
    (!Number.isFinite(Number(p.auctionStep)) || Number(p.auctionStep) <= 0)) errors.auctionStep = required;
  if (p.assignmentMethod === "DirectAssignment") {
    if (!p.isEdit && (!Number.isInteger(Number(p.copyCount ?? 1)) ||
      Number(p.copyCount ?? 1) < 1 || Number(p.copyCount ?? 1) > 15)) {
      errors.copyCount = required;
    }
    if (!p.partner?.expeditorCompanyId) errors.partner = required;
    if (!(Number(p.price) > 0) || !Number.isFinite(Number(p.price)) || Number(p.price) > 2000000000) {
      errors.price = t("ordersCreate.validation.specifyPriceError");
    }
    if (!p.acceptedCurrency) errors.acceptedCurrency = t("ordersCreate.validation.selectCurrency");
  } else {
    if (!p.partnersForTender?.length) errors.partnersForTender = required;
    if (!p.tenderDateEnd) errors.tenderDateEnd = required;
    if (!p.tenderEndTime) errors.tenderEndTime = required;
    if (!p.acceptedCurrency) errors.acceptedCurrency = t("ordersCreate.validation.selectCurrency");
    const deadline = getTenderDeadline(p.tenderDateEnd, p.tenderEndTime);
    if (p.tenderDateEnd && p.tenderEndTime && (!deadline || moment(deadline).isSameOrBefore(now))) {
      errors.tenderDateEnd = t("unifiedOrder.futureDeadline");
    }
  }
  p.points.forEach((point, index) => {
    const pointErrors = {};
    for (const name of ["country", "city", "address", "recipientName"]) {
      if (!point[name]) pointErrors[name] = required;
    }
    if (Object.keys(pointErrors).length) {
      errors.points = { ...errors.points, [`points_${index + 2}`]: pointErrors };
    }
  });
  return errors;
};
